import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Mail, Lock, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface AccountSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentEmail: string;
}

const AccountSettingsModal: React.FC<AccountSettingsModalProps> = ({
  isOpen,
  onClose,
  currentEmail,
}) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Email change state
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleEmailChange = async () => {
    if (!newEmail || !confirmEmail) {
      toast({
        title: "Missing Fields",
        description: "Please fill in all email fields.",
        variant: "destructive",
      });
      return;
    }

    if (newEmail !== confirmEmail) {
      toast({
        title: "Emails Don't Match",
        description: "The new email addresses must match.",
        variant: "destructive",
      });
      return;
    }

    if (newEmail === currentEmail) {
      toast({
        title: "Same Email",
        description: "The new email is the same as your current email.",
        variant: "destructive",
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.auth.updateUser({
        email: newEmail,
      });

      if (error) throw error;

      toast({
        title: "Verification Email Sent",
        description: "Please check your new email inbox and click the confirmation link to complete the change.",
      });

      setNewEmail('');
      setConfirmEmail('');
      onClose();
    } catch (error: any) {
      console.error('Error updating email:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!newPassword || !confirmPassword) {
      toast({
        title: "Missing Fields",
        description: "Please fill in all password fields.",
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords Don't Match",
        description: "The new passwords must match.",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Password Too Short",
        description: "Password must be at least 6 characters long.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast({
        title: "Password Updated",
        description: "Your password has been changed successfully.",
      });

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onClose();
    } catch (error: any) {
      console.error('Error updating password:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setNewEmail('');
    setConfirmEmail('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md bg-delphi-dark border-delphi-gray/30">
        <DialogHeader>
          <DialogTitle className="text-xl text-delphi-light">
            Account Settings
          </DialogTitle>
          <DialogDescription className="text-delphi-light/60">
            Update your login credentials.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="email" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-delphi-dark/50">
            <TabsTrigger value="email" className="data-[state=active]:bg-delphi-red">
              <Mail size={14} className="mr-2" />
              Email
            </TabsTrigger>
            <TabsTrigger value="password" className="data-[state=active]:bg-delphi-red">
              <Lock size={14} className="mr-2" />
              Password
            </TabsTrigger>
          </TabsList>

          <TabsContent value="email" className="mt-4 space-y-4">
            <div className="p-3 bg-delphi-gray/10 border border-delphi-gray/20 rounded-lg">
              <p className="text-sm text-delphi-light/70">
                Current email: <span className="text-delphi-light font-medium">{currentEmail}</span>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newEmail" className="text-delphi-light">New Email</Label>
              <Input
                id="newEmail"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="bg-delphi-dark/50 border-delphi-gray/30 text-delphi-light"
                placeholder="Enter new email address"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmEmail" className="text-delphi-light">Confirm New Email</Label>
              <Input
                id="confirmEmail"
                type="email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                className="bg-delphi-dark/50 border-delphi-gray/30 text-delphi-light"
                placeholder="Confirm new email address"
              />
            </div>

            <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <AlertCircle size={16} className="text-yellow-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-yellow-500/90">
                You will receive a confirmation email at your new address. Click the link to complete the change.
              </p>
            </div>

            <Button
              onClick={handleEmailChange}
              className="w-full bg-delphi-red hover:bg-delphi-red/90"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Email'
              )}
            </Button>
          </TabsContent>

          <TabsContent value="password" className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-delphi-light">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-delphi-dark/50 border-delphi-gray/30 text-delphi-light"
                placeholder="Enter new password"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-delphi-light">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="bg-delphi-dark/50 border-delphi-gray/30 text-delphi-light"
                placeholder="Confirm new password"
              />
            </div>

            <p className="text-xs text-delphi-light/50">
              Password must be at least 6 characters long.
            </p>

            <Button
              onClick={handlePasswordChange}
              className="w-full bg-delphi-red hover:bg-delphi-red/90"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Password'
              )}
            </Button>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            className="w-full border-delphi-gray/30 text-delphi-light hover:bg-delphi-gray/10"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AccountSettingsModal;
